import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { useParams } from '@tanstack/react-router';
import { bbox as turfBBOX } from '@turf/turf';
import { useEffect, useLayoutEffect, useState } from 'react';
import { MapProvider, useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import authorizations, { Action } from '~/authorizations';
import { type MapDetail, MapStyle, type TLoadedState } from '~/common/types';
import { MapComponent as CivilGridMap } from '~/components2/Map';
import type { InitialViewState } from '~/components2/Map/common';
import MapContextMenu from '~/components2/Map/Widgets/MapContextMenu/MapContextMenu';
import layerStore from '~/store2/layers';
import type { ProjectDetailsParams } from '..';
import { type CivilDrawStoreApi, CivilDrawStoreProvider } from '../civildraw/civilDrawStoreContext';
import EditorSurface from '../civildraw/surfaces/EditorSurface';
import Surface from '../civildraw/surfaces/Surface';
import Comments from '../civildraw/widgets/Comments';
import ObjectDetails from '../civildraw/widgets/ObjectDetails';
import ImageFullscreen from '../civildraw/widgets/ObjectDetails/ImagePreview/ImageFullscreen';
import ImageHoverPreview from '../civildraw/widgets/ObjectDetails/ImagePreview/ImageHoverPreview';
import StreetView from '../civildraw/widgets/StreetView';
import Toolbar from '../civildraw/widgets/Toolbar/Toolbar';
import { PermittingCondensedLegend } from './PermittingCondensedLegend';
import { PermittingNorthArrow } from './PermittingNorthArrow';
import permittingDrawStore from './permittingDrawStore';
import { PERMITTING_DEFAULT_METADATA_FIELD_IDS, PERMITTING_VISIBLE_UTILITY_LAYER_IDS } from './permittingEmbedLayers';
import { permittingEmbedMapRef } from './permittingMapSnapshot';

function PermittingMapSnapshotBridge() {
  const mapRef = useMap()?.current;
  useLayoutEffect(() => {
    permittingEmbedMapRef.current = mapRef ?? null;
    return () => {
      permittingEmbedMapRef.current = null;
    };
  }, [mapRef]);
  return null;
}

/**
 * Isolated CivilDraw instance for the permit map: drawings are kept in-memory across tab switches and
 * persisted to the permit draft (a mapType='permit' map) on Save Draft — they never appear in the
 * project's CivilDraw map list.
 */
export function EmbeddedCivilDrawMap() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { project, currentMap, currentMapId, mapView } = useSnapshot(permittingDrawStore) as typeof permittingDrawStore;
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMapReady(false);

    const previousVisible = new Set(layerStore.visibleLayerIds);
    const previousMetadata = new Set(layerStore.visibleMetadataIds);

    layerStore.visibleLayerIds.clear();
    for (const id of PERMITTING_VISIBLE_UTILITY_LAYER_IDS) {
      layerStore.visibleLayerIds.add(id);
    }

    layerStore.visibleMetadataIds.clear();
    for (const id of PERMITTING_DEFAULT_METADATA_FIELD_IDS) {
      layerStore.visibleMetadataIds.add(id);
    }

    layerStore.toggleTOC(false);
    layerStore.toggleMetadataPicker(false);

    (async () => {
      await permittingDrawStore.loadProject(projectId);
      if (cancelled) return;
      if (permittingDrawStore.project.isLoading || permittingDrawStore.currentMap.isLoading) return;
      setMapReady(true);
    })();

    return () => {
      cancelled = true;
      // Keep the in-memory draft alive across tab switches; the DB draft is the durable store.
      setMapReady(false);

      layerStore.visibleLayerIds.clear();
      for (const id of previousVisible) {
        layerStore.visibleLayerIds.add(id);
      }
      layerStore.visibleMetadataIds.clear();
      for (const id of previousMetadata) {
        layerStore.visibleMetadataIds.add(id);
      }
    };
  }, [projectId]);

  if (!mapReady || !project || project.isLoading || !currentMap || currentMap.isLoading) {
    return (
      <div className="relative w-full aspect-[16/9] overflow-hidden rounded border border-grey-30 bg-grey-10">
        <div className="absolute inset-0 flex items-center justify-center">
          <SkeletonSpinnerCentered />
        </div>
      </div>
    );
  }

  const projectBounds = turfBBOX(project.geometry as unknown as GeoJSON.Feature);

  // Restore the saved viewport when a draft was loaded; otherwise fit to the project bounds.
  const initialViewState: InitialViewState = mapView
    ? {
        longitude: mapView.center[0],
        latitude: mapView.center[1],
        zoom: mapView.zoom,
        bearing: mapView.bearing ?? 0,
        pitch: mapView.pitch ?? 0,
      }
    : {
        bounds: [projectBounds[0], projectBounds[1], projectBounds[2], projectBounds[3]],
        fitBoundsOptions: {
          padding: 100,
        },
      };

  const onBasemapChange = (baseMap: MapStyle) => {
    const cm = permittingDrawStore.currentMap;
    if (cm.isLoading !== false) return;
    (cm as MapDetail & TLoadedState).baseMap = baseMap;
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <CivilDrawStoreProvider store={permittingDrawStore as CivilDrawStoreApi}>
        <MapProvider>
          <div
            id="permitting-map-embed"
            className="relative w-full aspect-[16/9] overflow-hidden rounded border border-grey-30"
          >
            <div className="absolute inset-0 min-h-0">
              <CivilGridMap
                store={permittingDrawStore}
                initialViewState={initialViewState}
                persistViewportInLocalStorage={false}
                projectGeometry={project.geometry}
                projectCircle={project.circle?.geometry}
                mapStyle={currentMap.baseMap}
                showLayers={true}
                showLayerPicker={false}
                showScale={true}
                units={currentMap.unitSystem}
                showZoomButtons={true}
                showBasemapPicker={true}
                allowedBasemapStyles={[MapStyle.civilGridLight, MapStyle.satellite]}
                onBasemapChange={onBasemapChange}
                showLegend={false}
                includeDefaultContextMenu={false}
                projectId={projectId}
                currentMapId={currentMapId}
                id="permitting-civildraw-map"
                controls={{}}
                allowRotation={true}
                preserveDrawingBuffer={true}
                reuseMaps={false}
              >
                <PermittingMapSnapshotBridge />
                <PermittingNorthArrow />
                <MapContextMenu
                  store={permittingDrawStore}
                  allowComments={true}
                  allowObjectOrdering={true}
                  streetViewMode="internal"
                  projectId={projectId}
                  currentMapId={currentMapId}
                >
                  <Surface interactive={authorizations.can(Action.EditCivilDraw)} />
                  <EditorSurface />
                </MapContextMenu>

                <StreetView store={permittingDrawStore} />

                <ImageHoverPreview />
                <ImageFullscreen />
                <ObjectDetails />
                <Toolbar />
                <Comments />
              </CivilGridMap>
            </div>
          </div>
          <PermittingCondensedLegend />
        </MapProvider>
      </CivilDrawStoreProvider>
    </div>
  );
}
